import './styles.css';

import type {
  EdgeId,
  PathLookupDecision,
  PathLookupDeviceDecisions,
  ReportSeverity,
} from '@ipfabric/utils';
import classNames from 'classnames';
import type {
  DeviceViewEntityId,
  DeviceViewNodeEntity,
} from 'components/DeviceView/deviceViewTypes';
import { useLocale } from 'containers/App/LocaleContext';
import { type FC, memo, useEffect, useMemo } from 'react';

import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { getCloudConstructKindFromNode } from 'components/DeviceView/helpers/cloudConstruct';
import { NothingToSeeHere } from 'components/NothingToSeeHere/NothingToSeeHere';
import { SecuritySchemaModal } from 'components/SecuritySchema/SecuritySchemaModal';
import type { DecidingRuleScope } from 'components/SecuritySchema/model/SecuritySchemaEntryPoint';
import type { HostIdentifier } from 'model/HostIdentifier';
import { useLocation, useNavigate } from 'react-router-dom';
import type { AppTranslations } from 'translations/translations';
import {
  pushHistoryStateField,
  replaceHistoryStateField,
} from 'utils/history/stateFields';
import { useSnapshotId } from 'utils/hooks/useSnapshotId';
import { PacketDetailSeverityInfo } from '../PacketDetail/PacketDetailSeverityInfo';
import { PathInspectorTableRow } from './PathInspectorTableRow';
import {
  CloudDecisionTable,
  isFlatCloudConstructKind,
} from './cloud/CloudDecisionTable';

const nothingToSeeHereClassName =
  'pathlookup-tab-content text-center whitespace-nowrap px-10 py-5';

interface Props {
  decision: PathLookupDeviceDecisions | undefined;
  deviceViewEntity: DeviceViewNodeEntity;
  nodeTitle: string;
  severityInfo?: Record<ReportSeverity, number>;
  sourcePacketId: EdgeId;
  targetPacketId: EdgeId;
}

interface LocationState {
  securityCheckDetailScope: DecidingRuleScope | undefined;
  deviceViewEntityId: DeviceViewEntityId | undefined;
}

const emptyLocationState: LocationState = {
  securityCheckDetailScope: undefined,
  deviceViewEntityId: undefined,
};

const BarePathInspectorTable: FC<Props> = (props) => {
  const msg = useLocale();
  const snapshotId = useSnapshotId();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | undefined;
  const securityCheckDetailScope =
    state?.deviceViewEntityId === props.deviceViewEntity.id
      ? state?.securityCheckDetailScope
      : undefined;
  const tracesForInspection = props.decision?.traces.find(
    (trace) =>
      trace.sourcePacketId === props.sourcePacketId &&
      trace.targetPacketId === props.targetPacketId,
  );
  // NIM-25155 (vNIC), NIM-25156 (subnet) and NIM-25157 (peering) define the
  // flat cloud rows.
  const cloudConstructKind = getCloudConstructKindFromNode(
    props.deviceViewEntity.nodeType,
    props.deviceViewEntity.extra,
  );
  const flatCloudConstructKind = isFlatCloudConstructKind(cloudConstructKind)
    ? cloudConstructKind
    : null;

  const hostIdentifier: HostIdentifier | null = useMemo(() => {
    if (snapshotId === null) {
      return null;
    }
    const cloudKind = getCloudConstructKindFromNode(
      props.deviceViewEntity.nodeType,
      props.deviceViewEntity.extra,
    );
    if (cloudKind === 'networkInterface' || cloudKind === 'subnet') {
      return {
        cloudConstructId: props.deviceViewEntity.sn,
        snapshotId,
      };
    }
    return {
      hostSn: props.deviceViewEntity.sn,
      snapshotId,
    };
  }, [props.deviceViewEntity, snapshotId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: to be executed only on mount
  useEffect(() => {
    if (state?.securityCheckDetailScope) {
      // cleanup security check detail from history state on mount
      // because e.g. on page reload in graphs, graph app state is not preserved
      // and the modal would become irrelevant.
      replaceHistoryStateField(navigate, location, emptyLocationState);
    }
  }, []);

  const handleSecuritySchemaModalClose = useStableCallback(() => {
    pushHistoryStateField(navigate, location, emptyLocationState);
  });

  const handleShowSecurityCheckDetail = useStableCallback(
    (scope: DecidingRuleScope) => {
      pushHistoryStateField(navigate, location, {
        securityCheckDetailScope: scope,
        deviceViewEntityId: props.deviceViewEntity.id,
      });
    },
  );

  // A cloud construct keeps its decision table when the trace exists but
  // recorded nothing — "no decisions" is itself part of the spec.
  if (
    props.decision === undefined ||
    tracesForInspection === undefined ||
    (flatCloudConstructKind === null && tracesForInspection.trace.length === 0)
  ) {
    return <NothingToSeeHere className={nothingToSeeHereClassName} />;
  }

  return (
    <div
      className={classNames(
        'pathlookup-tab-content',
        // The on-prem grid's fixed column widths make it wide enough to fill
        // the inspector on its own; the flat cloud rows are only as wide as
        // their text, so the panel has to absorb the leftover width itself.
        flatCloudConstructKind !== null && 'flex-1',
      )}
    >
      <div className='flex p-5 font-bold'>
        <span className='flex-1'>
          {msg.deviceView.devicePathLookup.decisionTable.title.replace(
            '{nodeTitle}',
            props.nodeTitle,
          )}
        </span>
        {props.severityInfo !== undefined && (
          <PacketDetailSeverityInfo
            severityInfo={props.severityInfo}
            wrapperClassName='pathlookup-tab-content__severity-info-wrapper'
          />
        )}
      </div>
      {flatCloudConstructKind !== null ? (
        <CloudDecisionTable
          constructKind={flatCloudConstructKind}
          deviceViewEntityId={props.deviceViewEntity.id}
          onShowSecurityCheckDetail={handleShowSecurityCheckDetail}
          trace={tracesForInspection}
        />
      ) : (
        <OnPremDecisionTable
          deviceViewEntityId={props.deviceViewEntity.id}
          msg={msg}
          onShowSecurityCheckDetail={handleShowSecurityCheckDetail}
          traces={tracesForInspection.trace}
        />
      )}
      {securityCheckDetailScope && hostIdentifier && (
        <SecuritySchemaModal
          entryPoint={securityCheckDetailScope}
          highlightedRuleLabel={msg.deviceView.devicePathLookup.decidedByRule}
          hostIdentifier={hostIdentifier}
          onClose={handleSecuritySchemaModalClose}
        />
      )}
    </div>
  );
};

interface OnPremDecisionTableProps {
  deviceViewEntityId: DeviceViewEntityId;
  msg: AppTranslations;
  onShowSecurityCheckDetail: (scope: DecidingRuleScope) => void;
  traces: PathLookupDecision[];
}

/** The on-prem `Location | Decision | Filter/Data | Detail` grid, unchanged. */
const OnPremDecisionTable: FC<OnPremDecisionTableProps> = (props) => {
  const { msg } = props;
  return (
    <table className='h-lgu-1 table-fixed'>
      <thead>
        <tr>
          <td className='pathlookup-tab-content-cell__indent' />
          <td
            className={classNames(
              'pathlookup-tab-content-cell',
              'pathlookup-tab-content-cell__first',
              'pathlookup-tab-content-cell__heading',
              'font-semibold',
              'whitespace-nowrap',
            )}
          >
            {msg.deviceView.devicePathLookup.decisionTable.location}
          </td>
          <td className='pathlookup-tab-content-cell pathlookup-tab-content-cell__heading w-1/5 font-semibold'>
            {msg.deviceView.devicePathLookup.decisionTable.decision}
          </td>
          <td
            className={classNames(
              'pathlookup-tab-content-cell',
              'pathlookup-tab-content-cell__last',
              'pathlookup-tab-content-cell__heading',
              'font-semibold',
              'w-1/5',
            )}
          >
            {msg.deviceView.devicePathLookup.decisionTable.filter}
          </td>
          <td
            className={classNames(
              'pathlookup-tab-content-cell',
              'pathlookup-tab-content-cell__last',
              'pathlookup-tab-content-cell__heading',
              'font-semibold',
              'whitespace-nowrap',
              'w-1/5',
            )}
          >
            {msg.deviceView.devicePathLookup.decisionTable.packetAction}
          </td>
          <td className='pathlookup-tab-content-cell__indent' />
        </tr>
      </thead>
      <tbody>
        {props.traces.map((trace: PathLookupDecision, traceIndex: number) => (
          <PathInspectorTableRow
            deviceViewEntityId={props.deviceViewEntityId}
            // biome-ignore lint/suspicious/noArrayIndexKey: no better key
            key={traceIndex}
            onShowSecurityCheckDetail={props.onShowSecurityCheckDetail}
            trace={trace}
            traceIndex={traceIndex}
          />
        ))}
      </tbody>
    </table>
  );
};

export const PathInspectorTable = memo(BarePathInspectorTable);
